import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import analytics from "../lib/analytics";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Calculator as CalcIcon } from "lucide-react";
import BurnsDiagram from "../components/BurnsDiagram.jsx";

// Each compute() returns:
//   { value, unit, interpretation,                   // headline
//     formula,                                        // human readable formula
//     working: [ "step 1...", "step 2..." ]           // step-by-step substitution
//   }
// GCS is intentionally a score-only calculator (no clinical management advice).
function compute(calcId, v) {
  const num = (x) => parseFloat(x || 0);
  switch (calcId) {
    case "calc-bmi": {
      const h_cm = num(v.height);
      const w = num(v.weight);
      const h = h_cm / 100;
      if (!h || !w) return null;
      const bmi = w / (h * h);
      const cls = bmi < 18.5 ? "Underweight" : bmi < 25 ? "Normal" : bmi < 30 ? "Overweight" : bmi < 35 ? "Obese I" : bmi < 40 ? "Obese II" : "Obese III";
      return {
        value: bmi.toFixed(1), unit: "kg/m²", interpretation: cls,
        formula: "BMI = weight(kg) / height(m)²",
        working: [
          `height(m) = ${h_cm} cm / 100 = ${h.toFixed(2)} m`,
          `height² = ${h.toFixed(2)} × ${h.toFixed(2)} = ${(h * h).toFixed(4)} m²`,
          `BMI = ${w} / ${(h * h).toFixed(4)} = ${bmi.toFixed(1)} kg/m²`,
        ],
      };
    }
    case "calc-gfr": {
      // CKD-EPI 2021 race-free
      const cr_umol = num(v.creatinine);
      const cr = cr_umol / 88.4;
      const age = num(v.age);
      if (!cr || !age || !v.sex) return null;
      const female = v.sex === "female";
      const k = female ? 0.7 : 0.9;
      const alpha = female ? -0.241 : -0.302;
      const minv = Math.min(cr / k, 1);
      const maxv = Math.max(cr / k, 1);
      const gfr = 142 * Math.pow(minv, alpha) * Math.pow(maxv, -1.200) * Math.pow(0.9938, age) * (female ? 1.012 : 1);
      const stage = gfr >= 90 ? "G1 (Normal)" : gfr >= 60 ? "G2 (Mildly reduced)" : gfr >= 45 ? "G3a" : gfr >= 30 ? "G3b" : gfr >= 15 ? "G4 (Severe)" : "G5 (Kidney failure)";
      return {
        value: gfr.toFixed(0), unit: "mL/min/1.73m²", interpretation: stage,
        formula: "eGFR = 142 × min(Scr/κ,1)^α × max(Scr/κ,1)^-1.200 × 0.9938^age × [1.012 if female]   (CKD-EPI 2021, race-free)",
        working: [
          `Scr = ${cr_umol} µmol/L ÷ 88.4 = ${cr.toFixed(3)} mg/dL`,
          `κ = ${k}  (sex = ${v.sex}),   α = ${alpha}`,
          `min(Scr/κ,1) = ${minv.toFixed(3)},  max(Scr/κ,1) = ${maxv.toFixed(3)}`,
          `eGFR = 142 × ${Math.pow(minv, alpha).toFixed(3)} × ${Math.pow(maxv, -1.2).toFixed(3)} × ${Math.pow(0.9938, age).toFixed(3)}${female ? " × 1.012" : ""} = ${gfr.toFixed(0)} mL/min/1.73m²`,
        ],
      };
    }
    case "calc-apgar": {
      const keys = ["appearance", "pulse", "grimace", "activity", "respiration"];
      const parts = keys.map((k) => {
        const s = v[k] || "";
        const m = s.match(/\((\d)\)/);
        return { key: k, label: s.replace(/\s*\(\d\)\s*$/, ""), pts: m ? parseInt(m[1]) : null };
      });
      if (parts.some((p) => p.pts === null)) return null;
      const sum = parts.reduce((a, p) => a + p.pts, 0);
      const interp = sum >= 7 ? "Reassuring (7–10)" : sum >= 4 ? "Moderately depressed (4–6)" : "Severely depressed (0–3)";
      return {
        value: sum, unit: "/ 10", interpretation: interp,
        formula: "APGAR = Appearance + Pulse + Grimace + Activity + Respiration (each 0–2)",
        working: [...parts.map((p) => `${p.key}: ${p.label} = ${p.pts}`), `Sum = ${parts.map((p) => p.pts).join(" + ")} = ${sum} / 10`],
      };
    }
    case "calc-chadsvasc": {
      const keys = ["chf", "htn", "age", "dm", "stroke", "vasc", "sex"];
      const parts = keys.map((k) => {
        const s = v[k] || "";
        const m = s.match(/\((-?\d)\)/);
        return { key: k, label: s.replace(/\s*\(-?\d\)\s*$/, ""), pts: m ? parseInt(m[1]) : null };
      });
      if (parts.some((p) => p.pts === null)) return null;
      const sum = parts.reduce((a, p) => a + p.pts, 0);
      const interp = sum === 0 ? "Score 0 — low risk" : sum === 1 ? "Score 1 — borderline" : "Score ≥2 — moderate–high risk";
      return {
        value: sum, unit: "/ 9", interpretation: interp,
        formula: "CHA₂DS₂-VASc = C(1) + H(1) + A≥75(2) + D(1) + S/TIA(2) + V(1) + A 65–74(1) + Sex female(1)",
        working: [...parts.map((p) => `${p.key}: ${p.label} = ${p.pts}`), `Sum = ${sum} / 9`],
      };
    }
    case "calc-wells-dvt": {
      const keys = ["cancer", "paralysis", "bedridden", "tenderness", "swelling_leg", "calf", "pitting", "collateral", "alt_dx"];
      const parts = keys.map((k) => {
        const s = v[k] || "";
        const m = s.match(/\((-?\d)\)/);
        return { key: k, label: s.replace(/\s*\(-?\d\)\s*$/, ""), pts: m ? parseInt(m[1]) : null };
      });
      if (parts.some((p) => p.pts === null)) return null;
      const sum = parts.reduce((a, p) => a + p.pts, 0);
      const interp = sum >= 3 ? "High probability (≥3)" : sum >= 1 ? "Moderate probability (1–2)" : "Low probability (≤0)";
      return {
        value: sum, unit: "points", interpretation: interp,
        formula: "Wells' DVT = sum of weighted clinical items (alternative dx = −2)",
        working: [...parts.map((p) => `${p.label}: ${p.pts >= 0 ? "+" : ""}${p.pts}`), `Total = ${sum} points`],
      };
    }
    case "calc-qtc": {
      const qt = num(v.qt), rr = num(v.rr);
      if (!qt || !rr) return null;
      const rr_s = rr / 1000;
      const sqrt_rr = Math.sqrt(rr_s);
      const qtc = qt / sqrt_rr;
      const interp = qtc > 500 ? "Prolonged (>500 ms)" : qtc > 450 ? "Borderline (450–500 ms)" : "Normal (<450 ms)";
      return {
        value: qtc.toFixed(0), unit: "ms", interpretation: interp,
        formula: "QTc (Bazett) = QT / √(RR)   where RR is in seconds",
        working: [
          `RR = ${rr} ms = ${rr_s.toFixed(3)} s`,
          `√RR = ${sqrt_rr.toFixed(4)}`,
          `QTc = ${qt} / ${sqrt_rr.toFixed(4)} = ${qtc.toFixed(0)} ms`,
        ],
      };
    }
    case "calc-mdrd": {
      const h_cm = num(v.height);
      if (!h_cm || !v.sex) return null;
      const base = v.sex === "female" ? 45.5 : 50;
      const ibw = base + 0.9 * (h_cm - 152);
      return {
        value: ibw.toFixed(1), unit: "kg", interpretation: "Use for renally-cleared drug dosing (e.g. gentamicin). Use TBW if less than IBW.",
        formula: "IBW (Devine) = base + 0.9 × (height(cm) − 152)   base = 50 (M) or 45.5 (F)",
        working: [
          `Base = ${base} (${v.sex})`,
          `Δheight from 152 cm = ${h_cm} − 152 = ${(h_cm - 152).toFixed(1)} cm`,
          `IBW = ${base} + 0.9 × ${(h_cm - 152).toFixed(1)} = ${ibw.toFixed(1)} kg`,
        ],
      };
    }
    case "calc-gcs": {
      // SCORE-ONLY calculator. No clinical-management advice — that is for the
      // attending clinician. Result is the numerical GCS and severity category.
      const keys = ["eye", "verbal", "motor"];
      const parts = keys.map((k) => {
        const s = v[k] || "";
        const m = s.match(/\((\d)\)/);
        return { key: k, label: s.replace(/\s*\(\d\)\s*$/, ""), pts: m ? parseInt(m[1]) : null };
      });
      if (parts.some((p) => p.pts === null)) return null;
      const sum = parts.reduce((a, p) => a + p.pts, 0);
      const cat = sum >= 13 ? "Mild (13–15)" : sum >= 9 ? "Moderate (9–12)" : "Severe (3–8)";
      return {
        value: sum, unit: "/ 15", interpretation: cat,
        formula: "GCS = Eye (1–4) + Verbal (1–5) + Motor (1–6)",
        working: [
          `Eye = ${parts[0].pts}  (${parts[0].label})`,
          `Verbal = ${parts[1].pts}  (${parts[1].label})`,
          `Motor = ${parts[2].pts}  (${parts[2].label})`,
          `GCS = ${parts[0].pts} + ${parts[1].pts} + ${parts[2].pts} = ${sum} / 15  →  E${parts[0].pts}V${parts[1].pts}M${parts[2].pts}`,
        ],
      };
    }
    case "calc-news2": {
      const rr = num(v.rr), spo2 = num(v.spo2), sbp = num(v.sbp), hr = num(v.hr), temp = num(v.temp);
      if (!rr || !spo2 || !sbp || !hr || !temp || !v.o2 || !v.avpu) return null;
      const score = (x, ranges) => { for (const [lo, hi, pts] of ranges) if (x >= lo && x <= hi) return pts; return 0; };
      const s_rr   = score(rr,   [[0, 8, 3], [9, 11, 1], [12, 20, 0], [21, 24, 2], [25, 200, 3]]);
      const s_spo2 = score(spo2, [[0, 91, 3], [92, 93, 2], [94, 95, 1], [96, 100, 0]]);
      const s_o2   = parseInt((v.o2?.match(/\((\d)\)/) || [, 0])[1]) || 0;
      const s_sbp  = score(sbp,  [[0, 90, 3], [91, 100, 2], [101, 110, 1], [111, 219, 0], [220, 400, 3]]);
      const s_hr   = score(hr,   [[0, 40, 3], [41, 50, 1], [51, 90, 0], [91, 110, 1], [111, 130, 2], [131, 250, 3]]);
      const s_avpu = parseInt((v.avpu?.match(/\((\d)\)/) || [, 0])[1]) || 0;
      const s_temp = score(temp, [[0, 35, 3], [35.1, 36, 1], [36.1, 38, 0], [38.1, 39, 1], [39.1, 45, 2]]);
      const total = s_rr + s_spo2 + s_o2 + s_sbp + s_hr + s_avpu + s_temp;
      const interp = total >= 7 ? "Score ≥7 — high" : total >= 5 ? "Score 5–6 — medium" : total >= 3 ? "Score 3–4 — low–medium" : "Score 0–2 — low";
      return {
        value: total, unit: "/ 20", interpretation: interp,
        formula: "NEWS2 = RR + SpO₂ + O₂-supplement + SBP + HR + AVPU + Temp  (each banded 0–3)",
        working: [
          `Respiratory rate ${rr}/min → ${s_rr}`,
          `SpO₂ ${spo2}% → ${s_spo2}`,
          `Supplemental O₂ → ${s_o2}`,
          `SBP ${sbp} mmHg → ${s_sbp}`,
          `Heart rate ${hr}/min → ${s_hr}`,
          `AVPU → ${s_avpu}`,
          `Temp ${temp}°C → ${s_temp}`,
          `Total = ${s_rr} + ${s_spo2} + ${s_o2} + ${s_sbp} + ${s_hr} + ${s_avpu} + ${s_temp} = ${total}`,
        ],
      };
    }
    case "calc-burns-tbsa": {
      const parts = ["head", "arms", "chest", "back", "legs", "genitals"].map((k) => ({ k, v: num(v[k]) }));
      const tbsa = parts.reduce((a, p) => a + p.v, 0);
      const w = num(v.weight);
      if (!tbsa) return null;
      const parkland = w ? Math.round(tbsa * 4 * w) : null;
      const first8 = parkland ? Math.round(parkland / 2) : null;
      return {
        value: `${tbsa.toFixed(1)}%`, unit: "TBSA",
        interpretation: parkland ? `Parkland ${parkland} mL in 24h (½ in first 8h)` : "Enter weight for Parkland fluid calc",
        formula: "TBSA = Σ regions (Rule of Nines).  Parkland = 4 mL × kg × %TBSA over 24 h, ½ in first 8 h",
        working: [
          ...parts.filter((p) => p.v).map((p) => `${p.k}: ${p.v}%`),
          `TBSA = ${parts.filter((p) => p.v).map((p) => p.v).join(" + ")} = ${tbsa.toFixed(1)}%`,
          ...(w ? [
            `Parkland = 4 × ${w} × ${tbsa.toFixed(1)} = ${parkland} mL / 24 h`,
            `First 8 h = ${parkland} / 2 = ${first8} mL`,
          ] : ["Enter weight to compute Parkland fluid"]),
        ],
      };
    }
    case "calc-burns-paed": {
      const LB = {
        head:[19,17,13,11,9], neck:[2,2,2,2,2], ant_trunk:[13,13,13,13,13], post_trunk:[13,13,13,13,13],
        buttocks:[5,5,5,5,5], genitals:[1,1,1,1,1], upper_arms:[8,8,8,8,8], forearms:[6,6,6,6,6],
        hands:[5,5,5,5,5], thighs:[11,13,16,17,18], lower_legs:[10,10,11,12,13], feet:[7,7,7,7,7],
      };
      const ageIdx = { "0–1 year": 0, "1–4 years": 1, "5–9 years": 2, "10–14 years": 3, "≥15 years": 4 }[v.age_band];
      if (ageIdx === undefined) return null;
      const w = num(v.weight);
      let tbsa = 0;
      const lines = [];
      for (const k of Object.keys(LB)) {
        const pct = Math.min(100, Math.max(0, num(v[k])));
        if (pct) {
          const contribution = (pct / 100) * LB[k][ageIdx];
          tbsa += contribution;
          lines.push(`${k}: ${pct}% × ${LB[k][ageIdx]} = ${contribution.toFixed(2)}%`);
        }
      }
      if (!tbsa) return null;
      const parkland = w ? Math.round(3 * w * tbsa) : null;
      const first8 = parkland ? Math.round(parkland / 2) : null;
      let maintHourly = 0;
      if (w) {
        if (w <= 10) maintHourly = 4 * w;
        else if (w <= 20) maintHourly = 40 + 2 * (w - 10);
        else maintHourly = 60 + 1 * (w - 20);
      }
      const maint24 = maintHourly ? Math.round(maintHourly * 24) : null;
      return {
        value: `${tbsa.toFixed(1)}%`, unit: "TBSA",
        interpretation: parkland ? `Mod Parkland ${parkland} mL / 24h (½ in first 8h)` : "Enter weight for fluid calc",
        formula: "Paed Lund & Browder by age band.  Mod Parkland = 3 × kg × %TBSA over 24 h.  Maintenance = Holliday-Segar 4-2-1.",
        working: [
          ...lines,
          `Total TBSA = ${tbsa.toFixed(1)}%`,
          ...(w ? [
            `Mod Parkland = 3 × ${w} × ${tbsa.toFixed(1)} = ${parkland} mL / 24 h  (½ = ${first8} mL in first 8 h)`,
            `Maintenance Holliday-Segar = ${maintHourly.toFixed(1)} mL/h (${maint24} mL / 24 h)`,
          ] : ["Enter weight to compute fluids"]),
        ],
      };
    }
    case "calc-paed-fluid": {
      const w = num(v.weight);
      if (!w) return null;
      let hourly = 0;
      const lines = [];
      if (w <= 10) { hourly = 4 * w; lines.push(`Weight ≤10 kg: 4 mL/kg/h × ${w} = ${hourly} mL/h`); }
      else if (w <= 20) { hourly = 40 + 2 * (w - 10); lines.push(`Weight 10–20 kg: 40 + 2 × (${w} − 10) = ${hourly} mL/h`); }
      else { hourly = 60 + 1 * (w - 20); lines.push(`Weight >20 kg: 60 + 1 × (${w} − 20) = ${hourly} mL/h`); }
      lines.push(`24-hour total = ${hourly} × 24 = ${(hourly * 24).toFixed(0)} mL`);
      return {
        value: hourly.toFixed(1), unit: "mL/h", interpretation: `${(hourly * 24).toFixed(0)} mL / 24 h maintenance`,
        formula: "Holliday-Segar 4-2-1: 4 mL/kg/h for first 10 kg, +2 for next 10 kg, +1 thereafter",
        working: lines,
      };
    }
    case "calc-anion-gap": {
      const na = num(v.na), cl = num(v.cl), hco3 = num(v.hco3);
      if (!na || !cl || !hco3) return null;
      const ag = na - (cl + hco3);
      const interp = ag > 12 ? "Raised (>12)" : ag < 8 ? "Low (<8)" : "Normal (8–12)";
      return {
        value: ag.toFixed(1), unit: "mmol/L", interpretation: interp,
        formula: "Anion gap = Na⁺ − (Cl⁻ + HCO₃⁻)",
        working: [
          `Na⁺ = ${na}, Cl⁻ = ${cl}, HCO₃⁻ = ${hco3}`,
          `AG = ${na} − (${cl} + ${hco3}) = ${na} − ${cl + hco3} = ${ag.toFixed(1)} mmol/L`,
        ],
      };
    }
    case "calc-cockcroft": {
      const age = num(v.age), w = num(v.weight), cr = num(v.creat);
      if (!age || !w || !cr || !v.sex) return null;
      const raw = ((140 - age) * w) / (0.814 * cr);
      const crcl = v.sex === "female" ? raw * 0.85 : raw;
      const interp = crcl >= 90 ? "Normal (≥90)" : crcl >= 60 ? "Mild ↓ (60–89)" : crcl >= 30 ? "Moderate ↓ (30–59)" : crcl >= 15 ? "Severe ↓ (15–29)" : "Kidney failure (<15)";
      return {
        value: crcl.toFixed(0), unit: "mL/min", interpretation: interp,
        formula: "CrCl = ((140 − age) × weight) / (0.814 × Scr)   × 0.85 if female   (Scr in µmol/L)",
        working: [
          `(140 − age) = 140 − ${age} = ${140 - age}`,
          `Numerator = ${140 - age} × ${w} = ${((140 - age) * w).toFixed(0)}`,
          `Denominator = 0.814 × ${cr} = ${(0.814 * cr).toFixed(2)}`,
          `CrCl = ${((140 - age) * w).toFixed(0)} / ${(0.814 * cr).toFixed(2)} = ${raw.toFixed(1)} mL/min${v.sex === "female" ? `,  × 0.85 (female) = ${crcl.toFixed(1)}` : ""}`,
        ],
      };
    }
    // ===== Iteration 3 additions =====
    case "calc-paed-burn-fluid": {
      const w = num(v.weight), tbsa = num(v.tbsa);
      if (!w || !tbsa) return null;
      const parkland = Math.round(3 * w * tbsa);
      const first8 = Math.round(parkland / 2);
      let maintH = 0;
      if (w <= 10) maintH = 4 * w;
      else if (w <= 20) maintH = 40 + 2 * (w - 10);
      else maintH = 60 + 1 * (w - 20);
      const maint24 = Math.round(maintH * 24);
      const total24 = parkland + maint24;
      return {
        value: `${total24}`, unit: "mL / 24 h (resus + maintenance)",
        interpretation: `Parkland ${parkland} mL + Maintenance ${maint24} mL = ${total24} mL`,
        formula: "Mod Parkland = 3 × kg × %TBSA over 24 h (½ in first 8 h).  Maintenance = Holliday-Segar 4-2-1.",
        working: [
          `Mod Parkland = 3 × ${w} × ${tbsa} = ${parkland} mL / 24 h`,
          `First 8 h (½ Parkland) = ${first8} mL  →  next 16 h = ${first8} mL`,
          `Holliday-Segar maintenance = ${maintH.toFixed(1)} mL/h (${maint24} mL / 24 h)`,
          `Combined 24-h total = ${parkland} + ${maint24} = ${total24} mL`,
          `Use Ringer's lactate for resus; D5 ½NS or per local protocol for maintenance. Titrate to urine output ≥ 1 mL/kg/h (infants) / 0.5–1 (older children).`,
        ],
      };
    }
    case "calc-drip-adult": {
      const vol = num(v.volume), th = num(v.time_h), df = num(v.drop_factor);
      if (!vol || !th || !df) return null;
      const min = th * 60;
      const gtt = (vol * df) / min;
      const mlh = vol / th;
      return {
        value: gtt.toFixed(0), unit: "gtt / min",
        interpretation: `≈ ${mlh.toFixed(0)} mL/h`,
        formula: "gtt/min = (Volume mL × Drop factor) / (Time × 60)",
        working: [
          `Time = ${th} h = ${min.toFixed(0)} min`,
          `Numerator = ${vol} × ${df} = ${(vol * df).toFixed(0)}`,
          `gtt/min = ${(vol * df).toFixed(0)} / ${min.toFixed(0)} = ${gtt.toFixed(1)}  →  ${Math.round(gtt)} gtt/min`,
          `Equivalent rate ≈ ${mlh.toFixed(0)} mL/h`,
        ],
      };
    }
    case "calc-drip-paed": {
      const vol = num(v.volume), th = num(v.time_h);
      if (!vol || !th) return null;
      const min = th * 60;
      const gtt = (vol * 60) / min;  // micro-drip 60 gtt/mL
      return {
        value: gtt.toFixed(0), unit: "gtt / min  (= mL/h)",
        interpretation: `${gtt.toFixed(0)} micro-drops per minute (paediatric)`,
        formula: "Micro-drip = 60 gtt/mL  →  gtt/min = (Volume × 60) / (Time × 60) = mL/h",
        working: [
          `Time = ${th} h = ${min.toFixed(0)} min`,
          `gtt/min = (${vol} × 60) / ${min.toFixed(0)} = ${gtt.toFixed(1)}  →  ${Math.round(gtt)} gtt/min`,
          `(Numerically equal to mL/h on a micro-drip set.)`,
        ],
      };
    }
    case "calc-curb65": {
      const keys = ["confusion", "urea", "rr", "bp", "age"];
      const parts = keys.map((k) => {
        const s = v[k] || "";
        const m = s.match(/\((\d)\)/);
        return { key: k, label: s.replace(/\s*\(\d\)\s*$/, ""), pts: m ? parseInt(m[1]) : null };
      });
      if (parts.some((p) => p.pts === null)) return null;
      const sum = parts.reduce((a, p) => a + p.pts, 0);
      const interp = sum <= 1 ? "0–1: Low (out-patient possible)" : sum === 2 ? "2: Moderate (short admission)" : "≥3: Severe (consider ICU)";
      return {
        value: sum, unit: "/ 5", interpretation: interp,
        formula: "CURB-65 = Confusion + Urea>7 + RR≥30 + BP-low + Age≥65",
        working: [...parts.map((p) => `${p.label}: ${p.pts}`), `Sum = ${sum} / 5`],
      };
    }
    case "calc-heart": {
      const keys = ["history", "ecg", "age", "risk", "trop"];
      const parts = keys.map((k) => {
        const s = v[k] || "";
        const m = s.match(/\((\d)\)/);
        return { key: k, label: s.replace(/\s*\(\d\)\s*$/, ""), pts: m ? parseInt(m[1]) : null };
      });
      if (parts.some((p) => p.pts === null)) return null;
      const sum = parts.reduce((a, p) => a + p.pts, 0);
      const interp = sum <= 3 ? "0–3: Low (≈1.7% MACE — discharge possible)" : sum <= 6 ? "4–6: Moderate (≈16.6% MACE — admit)" : "7–10: High (≈50% MACE — aggressive)";
      return {
        value: sum, unit: "/ 10", interpretation: interp,
        formula: "HEART = History + ECG + Age + Risk factors + Troponin (each 0–2)",
        working: [...parts.map((p) => `${p.key}: ${p.label} = ${p.pts}`), `Sum = ${sum} / 10`],
      };
    }
    case "calc-ciwa": {
      const keys = ["nausea", "tremor", "sweats", "anxiety", "agitation", "tactile", "auditory", "visual", "headache", "orient"];
      const parts = keys.map((k) => ({ key: k, val: num(v[k]) }));
      if (parts.some((p) => v[p.key] === undefined || v[p.key] === "")) return null;
      const sum = parts.reduce((a, p) => a + p.val, 0);
      const interp = sum < 10 ? "<10: Mild — supportive care" : sum < 19 ? "10–18: Moderate — symptom-triggered benzo" : "≥19: Severe — aggressive benzo + monitor for DTs";
      return {
        value: sum, unit: "/ 67", interpretation: interp,
        formula: "CIWA-Ar = sum of 10 items (9 scored 0–7 + orientation 0–4)",
        working: [...parts.map((p) => `${p.key}: ${p.val}`), `Sum = ${sum}`],
      };
    }
    case "calc-mews": {
      const keys = ["sbp", "hr", "rr", "temp", "avpu"];
      const parts = keys.map((k) => {
        const s = v[k] || "";
        const m = s.match(/\((\d)\)/);
        return { key: k, label: s.replace(/\s*\(\d\)\s*$/, ""), pts: m ? parseInt(m[1]) : null };
      });
      if (parts.some((p) => p.pts === null)) return null;
      const sum = parts.reduce((a, p) => a + p.pts, 0);
      const interp = sum >= 5 ? "≥5: Urgent review" : sum >= 3 ? "3–4: Increase monitoring" : "0–2: Low";
      return {
        value: sum, unit: "/ 14", interpretation: interp,
        formula: "MEWS = SBP + HR + RR + Temp + AVPU (each banded)",
        working: [...parts.map((p) => `${p.key}: ${p.label} = ${p.pts}`), `Sum = ${sum}`],
      };
    }
    case "calc-pews": {
      const keys = ["behaviour", "cardio", "resp"];
      const parts = keys.map((k) => {
        const s = v[k] || "";
        const m = s.match(/\((\d)\)/);
        return { key: k, label: s.replace(/\s*\(\d\)\s*$/, ""), pts: m ? parseInt(m[1]) : null };
      });
      if (parts.some((p) => p.pts === null)) return null;
      const sum = parts.reduce((a, p) => a + p.pts, 0);
      const interp = sum >= 5 ? "≥5: Critical — escalate immediately" : sum >= 3 ? "3–4: Concern — increase frequency" : "0–2: Low";
      return {
        value: sum, unit: "/ 9", interpretation: interp,
        formula: "PEWS = Behaviour + CVS + Respiratory (each 0–3)",
        working: [...parts.map((p) => `${p.key}: ${p.label} = ${p.pts}`), `Sum = ${sum}`],
      };
    }
    case "calc-centor": {
      const keys = ["fever", "tonsils", "nodes", "cough", "age"];
      const parts = keys.map((k) => {
        const s = v[k] || "";
        const m = s.match(/\((-?\d)\)/);
        return { key: k, label: s.replace(/\s*\(-?\d\)\s*$/, ""), pts: m ? parseInt(m[1]) : null };
      });
      if (parts.some((p) => p.pts === null)) return null;
      const sum = parts.reduce((a, p) => a + p.pts, 0);
      const interp = sum <= 0 ? "≤0: Strep unlikely" : sum === 1 ? "1: 5–10%" : sum === 2 ? "2: 11–17%" : sum === 3 ? "3: 28–35%" : "≥4: ≥50% — consider empiric tx";
      return {
        value: sum, unit: "points", interpretation: interp,
        formula: "Centor + McIsaac age modifier",
        working: [...parts.map((p) => `${p.label}: ${p.pts}`), `Total = ${sum}`],
      };
    }
    case "calc-abcd2": {
      const keys = ["age", "bp", "clinical", "duration", "dm"];
      const parts = keys.map((k) => {
        const s = v[k] || "";
        const m = s.match(/\((\d)\)/);
        return { key: k, label: s.replace(/\s*\(\d\)\s*$/, ""), pts: m ? parseInt(m[1]) : null };
      });
      if (parts.some((p) => p.pts === null)) return null;
      const sum = parts.reduce((a, p) => a + p.pts, 0);
      const interp = sum <= 3 ? "0–3: Low (1% 2-day stroke risk)" : sum <= 5 ? "4–5: Moderate (4.1%)" : "6–7: High (8.1%)";
      return {
        value: sum, unit: "/ 7", interpretation: interp,
        formula: "ABCD² = Age≥60 + BP≥140/90 + Clinical + Duration + Diabetes",
        working: [...parts.map((p) => `${p.key}: ${p.label} = ${p.pts}`), `Sum = ${sum}`],
      };
    }
    case "calc-hasbled": {
      const keys = ["htn", "renal", "liver", "stroke", "bleeding", "inr", "elderly", "drugs", "alcohol"];
      const parts = keys.map((k) => {
        const s = v[k] || "";
        const m = s.match(/\((\d)\)/);
        return { key: k, label: s.replace(/\s*\(\d\)\s*$/, ""), pts: m ? parseInt(m[1]) : null };
      });
      if (parts.some((p) => p.pts === null)) return null;
      const sum = parts.reduce((a, p) => a + p.pts, 0);
      const interp = sum <= 1 ? "0–1: Low bleeding risk" : sum === 2 ? "2: Moderate" : "≥3: High — review anticoagulation";
      return {
        value: sum, unit: "/ 9", interpretation: interp,
        formula: "HAS-BLED = HTN + Renal + Liver + Stroke + Bleeding + Labile INR + Elderly + Drugs + Alcohol",
        working: [...parts.map((p) => `${p.key}: ${p.pts}`), `Sum = ${sum}`],
      };
    }
    case "calc-wells-pe": {
      const keys = ["dvt_signs", "alt_dx", "hr", "immobil", "prev_pe", "hemop", "cancer"];
      const parts = keys.map((k) => {
        const s = v[k] || "";
        const m = s.match(/\(([0-9.]+)\)/);
        return { key: k, label: s.replace(/\s*\([0-9.]+\)\s*$/, ""), pts: m ? parseFloat(m[1]) : null };
      });
      if (parts.some((p) => p.pts === null)) return null;
      const sum = parts.reduce((a, p) => a + p.pts, 0);
      const interp = sum <= 4 ? "≤4: PE unlikely → D-dimer" : "> 4: PE likely → CTPA";
      return {
        value: sum, unit: "points", interpretation: interp,
        formula: "Wells' PE — sum of 7 weighted items",
        working: [...parts.map((p) => `${p.label}: ${p.pts}`), `Sum = ${sum}`],
      };
    }
    case "calc-child-pugh": {
      const keys = ["enceph", "ascites", "bili", "alb", "inr"];
      const parts = keys.map((k) => {
        const s = v[k] || "";
        const m = s.match(/\((\d)\)/);
        return { key: k, label: s.replace(/\s*\(\d\)\s*$/, ""), pts: m ? parseInt(m[1]) : null };
      });
      if (parts.some((p) => p.pts === null)) return null;
      const sum = parts.reduce((a, p) => a + p.pts, 0);
      const klass = sum <= 6 ? "Class A (5–6) — well compensated, 1-yr survival ~100%" :
                    sum <= 9 ? "Class B (7–9) — significant compromise, 1-yr ~80%" :
                               "Class C (10–15) — decompensated, 1-yr ~45%";
      return {
        value: sum, unit: "/ 15", interpretation: klass,
        formula: "Sum of 5 items (encephalopathy, ascites, bilirubin, albumin, INR), each 1–3",
        working: [...parts.map((p) => `${p.key}: ${p.label} = ${p.pts}`), `Sum = ${sum}`],
      };
    }
    case "calc-meld-na": {
      const cr_raw = num(v.creat), bili = num(v.bili), inr = num(v.inr), na_raw = num(v.na);
      if (!cr_raw || !bili || !inr || !na_raw) return null;
      // MELD floors values at 1; cap creatinine at 4.
      const cr = Math.min(Math.max(cr_raw, 1), 4);
      const bl = Math.max(bili, 1);
      const ir = Math.max(inr, 1);
      const na = Math.min(Math.max(na_raw, 125), 137);
      const meld_raw = 9.57 * Math.log(cr) + 3.78 * Math.log(bl) + 11.2 * Math.log(ir) + 6.43;
      const meld = Math.round(meld_raw);
      const meld_na = Math.round(meld + 1.32 * (137 - na) - 0.033 * meld * (137 - na));
      const interp = meld_na < 10 ? "<10: 3-month mortality ~1.9%" :
                     meld_na < 20 ? "10–19: ~6%" :
                     meld_na < 30 ? "20–29: ~19.6%" :
                     meld_na < 40 ? "30–39: ~52.6%" : "≥40: ~71.3%";
      return {
        value: meld_na, unit: "MELD-Na", interpretation: interp,
        formula: "MELD = 9.57·ln(Cr) + 3.78·ln(bili) + 11.2·ln(INR) + 6.43.   MELD-Na = MELD + 1.32(137−Na) − 0.033·MELD·(137−Na).   Values floored at 1; Cr capped at 4; Na clamped 125–137.",
        working: [
          `Cr (capped) = ${cr.toFixed(2)} mg/dL  →  ln = ${Math.log(cr).toFixed(3)}`,
          `Bili (floored) = ${bl.toFixed(2)} mg/dL  →  ln = ${Math.log(bl).toFixed(3)}`,
          `INR (floored) = ${ir.toFixed(2)}  →  ln = ${Math.log(ir).toFixed(3)}`,
          `Na (clamped) = ${na}  →  (137 − Na) = ${137 - na}`,
          `MELD = 9.57×${Math.log(cr).toFixed(3)} + 3.78×${Math.log(bl).toFixed(3)} + 11.2×${Math.log(ir).toFixed(3)} + 6.43 = ${meld_raw.toFixed(1)}  →  ${meld}`,
          `MELD-Na = ${meld} + 1.32×${137 - na} − 0.033×${meld}×${137 - na} = ${meld_na}`,
        ],
      };
    }
    case "calc-corrected-ca": {
      const ca = num(v.ca), alb = num(v.alb);
      if (!ca || !alb) return null;
      const corr = ca + 0.02 * (40 - alb);
      const interp = corr < 2.10 ? "Low (hypocalcaemia)" : corr > 2.55 ? "High (hypercalcaemia)" : "Normal (2.10–2.55)";
      return {
        value: corr.toFixed(2), unit: "mmol/L", interpretation: interp,
        formula: "Corrected Ca = total Ca + 0.02 × (40 − albumin g/L)",
        working: [
          `Δalbumin from 40 = 40 − ${alb} = ${(40 - alb).toFixed(1)} g/L`,
          `Adjustment = 0.02 × ${(40 - alb).toFixed(1)} = ${(0.02 * (40 - alb)).toFixed(2)} mmol/L`,
          `Corrected = ${ca.toFixed(2)} + ${(0.02 * (40 - alb)).toFixed(2)} = ${corr.toFixed(2)} mmol/L`,
        ],
      };
    }
    case "calc-na-correction": {
      const cur = num(v.current), tgt = num(v.target), w = num(v.weight);
      if (!cur || !tgt || !w || !v.sex) return null;
      const factor = v.sex === "female" ? 0.5 : 0.6;
      const tbw = factor * w;
      const deficit = tbw * (tgt - cur);
      const delta = tgt - cur;
      const safe_h = (Math.abs(delta) > 0) ? Math.max(Math.abs(delta) / 0.5, 16) : null; // ≤0.5 mmol/L/h
      const safeNote = Math.abs(delta) > 10
        ? "WARNING: target change >10 mmol/L in 24 h — high risk of osmotic demyelination. Limit to ≤8–10 mmol/L over 24 h."
        : "Target change within safe limits. Keep rate ≤ 0.5 mmol/L/h.";
      return {
        value: deficit.toFixed(0), unit: "mmol Na deficit",
        interpretation: safeNote,
        formula: "Na deficit (mmol) = TBW × (target − current).  TBW = 0.6 (M) or 0.5 (F) × kg.  Safe rate ≤ 0.5 mmol/L/h (≤ 8–10 mmol/L per 24 h).",
        working: [
          `TBW = ${factor} × ${w} = ${tbw.toFixed(1)} L  (${v.sex})`,
          `ΔNa = target − current = ${tgt} − ${cur} = ${delta.toFixed(1)} mmol/L`,
          `Na deficit = ${tbw.toFixed(1)} × ${delta.toFixed(1)} = ${deficit.toFixed(0)} mmol`,
          ...(safe_h ? [`Minimum safe duration ≈ ${safe_h.toFixed(0)} h (correct ≤ 0.5 mmol/L/h)`] : []),
        ],
      };
    }
    case "calc-bishop": {
      const keys = ["dil", "eff", "stat", "cons", "pos"];
      const parts = keys.map((k) => {
        const s = v[k] || "";
        const m = s.match(/\((\d)\)/);
        return { key: k, label: s.replace(/\s*\(\d\)\s*$/, ""), pts: m ? parseInt(m[1]) : null };
      });
      if (parts.some((p) => p.pts === null)) return null;
      const sum = parts.reduce((a, p) => a + p.pts, 0);
      const interp = sum >= 8 ? "≥8: Favourable — vaginal delivery likely" : sum >= 6 ? "6–7: Moderate" : "≤5: Unfavourable — consider cervical ripening";
      return {
        value: sum, unit: "/ 13", interpretation: interp,
        formula: "Bishop = Dilation + Effacement + Station + Consistency + Position",
        working: [...parts.map((p) => `${p.key}: ${p.label} = ${p.pts}`), `Sum = ${sum}`],
      };
    }
    case "calc-aa-gradient": {
      const fio2 = num(v.fio2), patm = num(v.patm), paco2 = num(v.paco2), pao2 = num(v.pao2), age = num(v.age);
      if (!fio2 || !patm || !paco2 || !pao2 || !age) return null;
      const pAO2 = fio2 * (patm - 47) - paco2 / 0.8;
      const aa = pAO2 - pao2;
      const expected = age / 4 + 4;
      const interp = aa > expected
        ? `Elevated (expected ≤ ${expected.toFixed(0)} mmHg) — V/Q mismatch, shunt, diffusion defect`
        : "Within normal range — hypoventilation or low FiO₂";
      return {
        value: aa.toFixed(0), unit: "mmHg", interpretation: interp,
        formula: "A–a = PAO₂ − PaO₂.   PAO₂ = FiO₂ × (Patm − 47) − PaCO₂/0.8.   Expected ≈ age/4 + 4.",
        working: [
          `PAO₂ = ${fio2} × (${patm} − 47) − ${paco2}/0.8`,
          `     = ${fio2} × ${patm - 47} − ${(paco2 / 0.8).toFixed(1)}`,
          `     = ${(fio2 * (patm - 47)).toFixed(1)} − ${(paco2 / 0.8).toFixed(1)} = ${pAO2.toFixed(1)} mmHg`,
          `A–a = ${pAO2.toFixed(1)} − ${pao2} = ${aa.toFixed(1)} mmHg`,
          `Expected upper limit ≈ ${age}/4 + 4 = ${expected.toFixed(1)} mmHg`,
        ],
      };
    }
    default:
      return null;
  }
}

export default function CalculatorDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [vals, setVals] = useState({});
  const [result, setResult] = useState(null);
  const usedFired = useRef(false);

  useEffect(() => {
    usedFired.current = false;
    setVals({});
    setResult(null);
    api.get(`/calculators/${id}`).then((r) => {
      setC(r.data);
      api.post("/recent", { item_type: "calculator", item_id: r.data.id, title: r.data.name }).catch(() => {});
    });
  }, [id]);

  const onChange = (fid, v) => {
    const next = { ...vals, [fid]: v };
    setVals(next);
    const r = compute(id, next);
    setResult(r);
    // GA4 calculator_used — once per calculator visit, on first valid result.
    if (r && !usedFired.current && c) {
      usedFired.current = true;
      analytics.calculatorUsed(c.name);
    }
  };

  if (!c) return <div className="text-slate-500 text-sm">Loading calculator...</div>;

  // Interactive burns calculators (body-diagram)
  if (id === "calc-burns-tbsa" || id === "calc-burns-paed") {
    const mode = id === "calc-burns-paed" ? "paed" : "adult";
    return (
      <div className="max-w-5xl mx-auto space-y-6" data-testid="calculator-detail">
        <Link to="/app/calculators" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> Back to calculators
        </Link>
        <div>
          <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">{c.category}</div>
          <h1 className="font-heading text-3xl font-bold text-[#111827] mt-1">{c.name}</h1>
          <p className="text-slate-600 text-sm mt-2">{c.description}</p>
        </div>
        <BurnsDiagram mode={mode} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="calculator-detail">
      <Link to="/app/calculators" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back to calculators
      </Link>

      <div>
        <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">{c.category}</div>
        <h1 className="font-heading text-3xl font-bold text-[#111827] mt-1">{c.name}</h1>
        <p className="text-slate-600 text-sm mt-2">{c.description}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <Card className="border-slate-200 md:col-span-2">
          <CardContent className="p-6 space-y-4">
            {c.fields.map((f) => (
              <div key={f.id}>
                <Label htmlFor={f.id}>{f.label}</Label>
                {f.type === "number" ? (
                  <Input
                    id={f.id}
                    type="number"
                    step="any"
                    min={f.min}
                    max={f.max}
                    value={vals[f.id] || ""}
                    onChange={(e) => onChange(f.id, e.target.value)}
                    className="mt-1.5"
                    data-testid={`calc-field-${f.id}`}
                  />
                ) : (
                  <Select value={vals[f.id] || ""} onValueChange={(v) => onChange(f.id, v)}>
                    <SelectTrigger className="mt-1.5" data-testid={`calc-field-${f.id}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className={`border ${result ? "border-[#0B5FFF] bg-[#EFF4FF]/40" : "border-slate-200 bg-slate-50"}`}>
          <CardContent className="p-6">
            <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">Result</div>
            {result ? (
              <>
                <div className="mt-3">
                  <div className="font-heading text-4xl font-extrabold text-[#111827] leading-none" data-testid="calc-result-value">
                    {result.value}
                  </div>
                  <div className="text-sm text-slate-600 mt-1">{result.unit}</div>
                </div>
                <div className="mt-4 inline-flex items-center px-3 py-1.5 rounded-md bg-white border border-[#0B5FFF]/30 text-sm font-semibold text-[#111827]" data-testid="calc-interpretation">
                  {result.interpretation}
                </div>
              </>
            ) : (
              <div className="mt-3 text-sm text-slate-500">Enter all values to see result.</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Show working / formula — visible for every calculator */}
      <Card className="border-slate-200" data-testid="calc-working">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-slate-500 font-semibold">
            <CalcIcon className="w-3.5 h-3.5" /> Show your working
          </div>
          <div className="mt-3 space-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Formula</div>
              <div className="text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2 font-mono text-slate-800 break-words">
                {result?.formula || c.formula || "—"}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Step-by-step substitution</div>
              {result?.working?.length ? (
                <ol className="space-y-1.5 list-decimal list-inside text-sm text-slate-700">
                  {result.working.map((step, i) => (
                    <li key={i} className="font-mono" data-testid={`calc-working-step-${i}`}>{step}</li>
                  ))}
                </ol>
              ) : (
                <div className="text-xs text-slate-500">
                  Enter all required values above to see the calculation worked through, step by step, using your inputs.
                </div>
              )}
            </div>
            <p className="text-[11px] text-slate-500 border-t border-slate-100 pt-3">
              This calculator returns the score / value only. <strong>Interpretation and any clinical action must be approved by a qualified medical practitioner.</strong>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
